import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import MySolutionListItem from './MySolutionListItem';
import BlockedBanner from '../../Solutions/components/community/BlockedBanner';
import AddEditSolutionModal from '../../Solutions/components/community/AddEditSolutionModal';
import GuidelinesModal from '../../Solutions/components/community/GuidelinesModal';
import { getFileExtensionForBlob } from '../../Solutions/components/community/imageProcessing';

const COMMUNITY_SOLUTION_BASE_URL = '/api/v1/community-solution';
const BASE_URL = `${COMMUNITY_SOLUTION_BASE_URL}/my-solutions`;
const PAGE_SIZE = 10;

const MySolutions = ({ onBack }) => {
  const [solutions, setSolutions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [writeAccess, setWriteAccess] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [editingSolution, setEditingSolution] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const userEmail = JSON.parse(localStorage.getItem('userProfile') || 'null')?.email;

  const mergeSubjects = (newSolutions) => {
    setSubjects((prev) => {
      const bySubjectId = new Map(prev.map((s) => [s.subjectId, s]));
      newSolutions.forEach((solution) => {
        const subjectId = solution?.subject?.subjectId;
        const subjectTitle = solution?.subject?.subjectTitle;
        if (subjectId && subjectTitle && !bySubjectId.has(subjectId)) {
          bySubjectId.set(subjectId, { subjectId, subjectTitle });
        }
      });
      return Array.from(bySubjectId.values());
    });
  };

  const fetchSolutions = async (nextOffset, subjectId, append) => {
    if (append) setIsLoadingMore(true);
    else setIsLoading(true);

    try {
      const params = { limit: PAGE_SIZE, offset: nextOffset };
      if (subjectId) params.subjectId = subjectId;

      const res = await axios.get(BASE_URL, { params });
      const fetched = res?.data?.data?.solutions ?? [];

      setSolutions((prev) => (append ? [...prev, ...fetched] : fetched));
      setOffset(nextOffset);
      setHasMore(fetched.length === PAGE_SIZE);
      setWriteAccess(res?.data?.data?.hasWriteAccess ?? null);

      if (!subjectId) mergeSubjects(fetched);
    } catch (error) {
      if (!append) setSolutions([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchMetaData = async () => {
    try {
      const res = await axios.get(`${COMMUNITY_SOLUTION_BASE_URL}/meta-data`);
      setMetaData(res?.data?.data ?? null);
    } catch (error) {
      setMetaData(null);
    }
  };

  useEffect(() => {
    fetchSolutions(0, null, false);
    fetchMetaData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectSubject = (subjectId) => {
    if (subjectId === selectedSubjectId) return;
    setSelectedSubjectId(subjectId);
    setExpandedId(null);
    fetchSolutions(0, subjectId, false);
  };

  const handleLoadMore = () => fetchSolutions(offset + PAGE_SIZE, selectedSubjectId, true);

  const handleToggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const handleCloseEditModal = () => setEditingSolution(null);

  const handleSubmitEdit = async (blobs) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      blobs.forEach((blob, index) =>
        formData.append('images', blob, `solution-${index + 1}.${getFileExtensionForBlob(blob)}`),
      );

      await axios.patch(`${COMMUNITY_SOLUTION_BASE_URL}/${editingSolution.questionId._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Solution submitted for re-review');
      setEditingSolution(null);
      fetchSolutions(0, selectedSubjectId, false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--q3-surface-default)]">
      {onBack && (
        <div className="sticky top-0 z-50 bg-[var(--q3-surface-default)] border-b border-[var(--q3-stroke-normal)] md:hidden">
          <div className="flex items-center gap-3 p-4">
            <button onClick={onBack} className="p-1">
              <ArrowLeftIcon size={20} className="text-[var(--q3-neutral-default)]" />
            </button>
            <div className="flex flex-col gap-1">
              <h1 className="text-sm font-semibold text-[var(--q3-neutral-default)]">My Solutions</h1>
              <p className="text-xs text-[var(--q3-neutral-light)]">View all the community solutions you have posted</p>
            </div>
          </div>
        </div>
      )}

      <div className="w-full mx-auto px-4 py-6 md:px-0 md:py-10 md:pl-6">
        <div className="hidden md:block mb-1 ml-6 -mt-8">
          <h1 className="text-2xl font-bold text-[var(--q3-neutral-default)]">My Solutions</h1>
          <p className="text-sm text-[var(--q3-neutral-light)]">View all the community solutions you have posted</p>
        </div>

        <div className="w-full mx-auto md:p-6 space-y-4">
          {writeAccess?.uploadBlocked && <BlockedBanner reason={writeAccess?.reason} userEmail={userEmail} />}

          {subjects.length > 0 && (
            <div className="flex flex-row flex-nowrap items-center gap-2 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => handleSelectSubject(null)}
                className={`cursor-pointer whitespace-nowrap shrink-0 text-xs md:text-sm font-medium px-3 py-1.5 rounded-full ${
                  selectedSubjectId === null
                    ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]'
                    : 'border border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)]'
                }`}
              >
                All
              </button>
              {subjects.map((subject) => (
                <button
                  key={subject.subjectId}
                  type="button"
                  onClick={() => handleSelectSubject(subject.subjectId)}
                  className={`cursor-pointer whitespace-nowrap shrink-0 text-xs md:text-sm font-medium px-3 py-1.5 rounded-full ${
                    selectedSubjectId === subject.subjectId
                      ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]'
                      : 'border border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)]'
                  }`}
                >
                  {subject.subjectTitle}
                </button>
              ))}
            </div>
          )}

          {isLoading ? (
            <div className="py-16 text-center text-sm text-[var(--q3-neutral-light)]">Loading...</div>
          ) : solutions.length > 0 ? (
            <div className="flex flex-col gap-3">
              {solutions.map((solution) => (
                <MySolutionListItem
                  key={solution._id}
                  solution={solution}
                  expanded={expandedId === solution._id}
                  onToggle={() => handleToggle(solution._id)}
                  hasWriteAccess={!writeAccess?.uploadBlocked}
                  maxEditCount={metaData?.maxEditCount}
                  onEdit={() => setEditingSolution(solution)}
                  userEmail={userEmail}
                />
              ))}

              {hasMore && (
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="cursor-pointer self-center text-sm font-medium text-[var(--q3-accent-normal)] px-4 py-2 disabled:opacity-50"
                >
                  {isLoadingMore ? 'Loading...' : 'Load more'}
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
              <span className="text-[var(--q3-neutral-default)] font-semibold">No solutions yet</span>
              <p className="text-sm text-[var(--q3-neutral-light)] max-w-[392px]">
                Solutions you post on community questions will show up here.
              </p>
            </div>
          )}
        </div>
      </div>

      {editingSolution && (
        <AddEditSolutionModal
          open
          isEdit
          showReverifyWarning={editingSolution.status === 'approved'}
          onClose={handleCloseEditModal}
          onSubmit={handleSubmitEdit}
          isSubmitting={isSubmitting}
          onViewGuidelines={() => setShowGuidelines(true)}
        />
      )}

      <GuidelinesModal
        open={showGuidelines}
        onClose={() => setShowGuidelines(false)}
        guidelines={metaData?.guideline}
      />
    </div>
  );
};

export default MySolutions;
